import {DatePicker} from '@github-ui/date-picker'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clsx} from 'clsx'
import {useCallback, useMemo, useState} from 'react'

import {formatDateString} from '../../utils/date-formatting'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import fieldEditorsStyles from './FieldEditors.module.css'
import styles from './IssueFieldItem.module.css'

// Normalized hour for timezone-safe date storage (noon UTC)
export const NORMALIZED_HOUR = 12

const DateDisplay = ({formattedDate}: {formattedDate: string}) =>
  formattedDate ? (
    <span className={fieldEditorsStyles.dateText}>{formattedDate}</span>
  ) : (
    <span className={fieldEditorsStyles.noValueContainer}>{LABELS.setValue}</span>
  )

export type IssueFieldDateEditorBaseProps = {
  fieldId: string
  fieldName: string
  value: Date | null
  onCommit: (fieldId: string, value: Date | null) => void
  shouldOpen?: boolean
  isDraft?: boolean
}

export type IssueFieldDateEditorProps = IssueFieldDateEditorBaseProps & {
  readonly: boolean
  errorPresent?: boolean
  className?: string
}

export type IssueFieldCompactDateEditorInternalProps = IssueFieldDateEditorBaseProps & {
  readonly: boolean
  className?: string
}

export const IssueFieldCompactDateEditorInternal = ({
  fieldId,
  fieldName,
  value,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  readonly,
  className,
}: IssueFieldCompactDateEditorInternalProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(shouldOpen)

  const formattedDate = useMemo(() => {
    if (!value) return ''
    return formatDateString(value)
  }, [value])

  // Convert UTC-normalized date to local date for DatePicker display
  const datePickerValue = useMemo(() => {
    if (!value) return null
    // Extract UTC date components and create local Date with same calendar date
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [value])

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
    setIsOpen(false)
  }, [isDraft, fieldId, onCommit])

  const handleDateChange = useCallback(
    (date: Date | null) => {
      // Normalize the date to avoid timezone issues
      let normalizedDate: Date | null = null
      if (date) {
        normalizedDate = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0),
        )
      }
      onCommit(fieldId, normalizedDate)
      setIsOpen(false)
    },
    [fieldId, onCommit],
  )

  const displayValue = formattedDate || LABELS.setValue

  if (readonly) {
    return (
      <div className={clsx(styles.issueFieldFormWrapper, className)}>
        <p className={styles.issueFieldLabel}>{fieldName}</p>
        <div className={styles.issueFieldValueText}>{displayValue}</div>
      </div>
    )
  }

  return (
    <DatePicker
      variant="single"
      showClearButton
      value={datePickerValue}
      onChange={handleDateChange}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={onClose}
      anchor={anchorProps => (
        <button
          {...anchorProps}
          className={clsx(
            styles.issueFieldFormWrapper,
            styles.issueFieldFormWrapperClickable,
            isOpen && styles.isEditing,
            className,
          )}
          aria-label={value ? `Edit ${fieldName}` : `Set ${fieldName}`}
        >
          <span className={styles.issueFieldLabel}>{fieldName}</span>
          <span className={styles.issueFieldValueText}>{displayValue}</span>
        </button>
      )}
    />
  )
}

export const IssueFieldDateEditor = ({
  fieldId,
  fieldName,
  value,
  readonly,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  errorPresent = false,
  className,
}: IssueFieldDateEditorProps) => {
  const isCompactViewEnabled = isFeatureEnabled('issue_fields_compact_view')

  const formattedDate = useMemo(() => {
    if (!value) return ''
    return formatDateString(value)
  }, [value])

  // Convert UTC-normalized date to local date for DatePicker display
  // The value prop is a Date at noon UTC, but DatePicker needs a local Date
  // with the correct calendar day to highlight the right day in the picker
  const datePickerValue = useMemo(() => {
    if (!value) return null
    // Extract UTC date components and create local Date with same calendar date
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [value])

  const [isOpen, setIsOpen] = useState(shouldOpen)

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
    setIsOpen(false)
  }, [isDraft, onCommit, fieldId])

  const handleChange = (date: Date | null) => {
    // Normalize the date to avoid timezone issues before passing to onCommit
    // This ensures the selected date is preserved regardless of user timezone
    let normalizedDate: Date | null = null
    if (date) {
      // Create a new date using the same year, month, and day but normalized to avoid timezone shifts
      normalizedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0))
    }

    onCommit(fieldId, normalizedDate)
    setIsOpen(false)
  }

  if (isCompactViewEnabled) {
    return (
      <IssueFieldCompactDateEditorInternal
        fieldId={fieldId}
        fieldName={fieldName}
        value={value}
        onCommit={onCommit}
        shouldOpen={shouldOpen}
        isDraft={isDraft}
        readonly={readonly}
        className={className}
      />
    )
  }

  return (
    <Section
      sectionHeader={
        <SectionHeader
          title={fieldName}
          readonly={readonly}
          buttonProps={readonly ? undefined : {onClick: () => setIsOpen(true)}}
          errorPresent={errorPresent}
        />
      }
      className={className}
    >
      {readonly ? (
        <div className={fieldEditorsStyles.dateAnchorContainer}>
          <DateDisplay formattedDate={formattedDate} />
        </div>
      ) : (
        <DatePicker
          key={fieldName}
          anchor={props => (
            <div
              {...props}
              className={`${fieldEditorsStyles.dateAnchorContainer} ${fieldEditorsStyles.dateAnchorContainerInteractive}`}
            >
              <DateDisplay formattedDate={formattedDate} />
            </div>
          )}
          showClearButton
          showTodayButton
          variant="single"
          dateFormat="long"
          onChange={handleChange}
          value={datePickerValue}
          placeholder="Set date"
          open={isOpen}
          onOpen={() => setIsOpen(true)}
          onClose={onClose}
        />
      )}
    </Section>
  )
}

try{ DateDisplay.displayName ||= 'DateDisplay' } catch {}
try{ IssueFieldCompactDateEditorInternal.displayName ||= 'IssueFieldCompactDateEditorInternal' } catch {}
try{ IssueFieldDateEditor.displayName ||= 'IssueFieldDateEditor' } catch {}